if (!customElements.get('up2-pricing')) {
  class Up2Pricing extends HTMLElement {
    connectedCallback() {
      this.cycleInputs = [...this.querySelectorAll('[name="pricing-cycle"]')];
      this.planInputs = [...this.querySelectorAll('[name="pricing-plan"]')];
      this.seatsInput = this.querySelector('[name="seats"]');
      this.locationsInput = this.querySelector('[name="locations"]');
      this.seatsField = this.querySelector('[data-pricing-seats-field]');
      this.seatsHint = this.querySelector('[data-pricing-seats-hint]');
      this.addonRow = this.querySelector('[data-pricing-addon-row]');
      this.saveRow = this.querySelector('[data-pricing-save-row]');
      this.effectiveRow = this.querySelector('[data-pricing-effective-row]');
      this.hint = this.querySelector('[data-pricing-locations-hint]');
      this.displays = {
        seats: this.querySelector('[data-pricing-display="seats"]'),
        locations: this.querySelector('[data-pricing-display="locations"]'),
        planAmount: this.querySelector('[data-pricing-plan-amount]'),
        included: this.querySelector('[data-pricing-included]'),
        addon: this.querySelector('[data-pricing-addon]'),
        total: this.querySelector('[data-pricing-total]'),
        period: this.querySelector('[data-pricing-total-period]'),
        effective: this.querySelector('[data-pricing-effective]'),
        save: this.querySelector('[data-pricing-save]'),
      };
      this.cardAmounts = {
        plus: this.querySelector('[data-pricing-amount="plus"]'),
        teams: this.querySelector('[data-pricing-amount="teams"]'),
      };
      this.cardPeriods = {
        plus: this.querySelector('[data-pricing-period="plus"]'),
        teams: this.querySelector('[data-pricing-period="teams"]'),
      };
      this.cardFrom = {
        teams: this.querySelector('[data-pricing-from="teams"]'),
      };
      this.cardDeals = [...this.querySelectorAll('[data-pricing-year-deal]')];
      this.copy = this.readCopy();
      this.locale = this.dataset.locale || 'de';
      this.lastTeamsSeats = this.clamp(this.number(this.seatsInput), this.minSeats(), this.maxSeats());
      this.addEventListener('input', () => this.render());
      this.addEventListener('change', () => this.render());
      this.render();
    }

    readCopy() {
      const node = this.querySelector('[data-pricing-copy]');
      try {
        return JSON.parse(node?.textContent || '{}');
      } catch {
        return {};
      }
    }

    number(input) {
      const value = Number.parseFloat(String(input?.value || '').replace(',', '.'));
      return Number.isFinite(value) ? value : 0;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    cycle() {
      return this.cycleInputs.find((input) => input.checked)?.value === 'year' ? 'year' : 'month';
    }

    plan() {
      return this.planInputs.find((input) => input.checked)?.value === 'plus' ? 'plus' : 'teams';
    }

    minSeats() {
      return Number.parseInt(this.dataset.minSeats || '3', 10);
    }

    maxSeats() {
      return Number.parseInt(this.dataset.maxSeats || '50', 10);
    }

    maxLocations() {
      return Number.parseInt(this.dataset.maxLocations || '20', 10);
    }

    includedFor(plan) {
      if (plan === 'plus') return Number.parseInt(this.dataset.plusIncluded || '1', 10);
      return Number.parseInt(this.dataset.teamsIncluded || '2', 10);
    }

    unitFor(plan, cycle) {
      if (plan === 'plus') {
        return cycle === 'year'
          ? Number.parseFloat(this.dataset.plusYear || '90')
          : Number.parseFloat(this.dataset.plusMonth || '9');
      }
      return cycle === 'year'
        ? Number.parseFloat(this.dataset.teamsYear || '70')
        : Number.parseFloat(this.dataset.teamsMonth || '7');
    }

    addonUnit(cycle) {
      return cycle === 'year'
        ? Number.parseFloat(this.dataset.addonYear || '150')
        : Number.parseFloat(this.dataset.addonMonth || '15');
    }

    money(value) {
      const rounded = Math.round(value * 100) / 100;
      const whole = Math.abs(rounded - Math.round(rounded)) < 1e-9;
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(rounded);
    }

    integer(value) {
      return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 0 }).format(value);
    }

    replace(template, map) {
      return Object.entries(map).reduce(
        (text, [key, value]) => text.replaceAll(`%${key}%`, String(value)),
        template || '',
      );
    }

    paintSlider(input) {
      if (!input) return;
      const min = Number.parseFloat(input.min);
      const max = Number.parseFloat(input.max);
      const value = this.number(input);
      const progress = max === min ? 0 : ((value - min) / (max - min)) * 100;
      input.style.setProperty('--pricing-progress', `${Math.min(100, Math.max(0, progress))}%`);
    }

    compute(plan, cycle, requestedSeats, locations) {
      const included = this.includedFor(plan);
      const unit = this.unitFor(plan, cycle);
      const seats = plan === 'plus' ? 1 : Math.max(this.minSeats(), requestedSeats);
      const planAmount = plan === 'plus' ? unit : seats * unit;
      const extraLocations = Math.max(0, locations - included);
      const addon = extraLocations * this.addonUnit(cycle);
      const total = planAmount + addon;
      const monthlyUnit = this.unitFor(plan, 'month');
      const monthlyPlan = plan === 'plus' ? monthlyUnit : seats * monthlyUnit;
      const monthlyAddon = extraLocations * this.addonUnit('month');
      const monthlyTotal = monthlyPlan + monthlyAddon;
      const yearlyTotal =
        (plan === 'plus' ? this.unitFor(plan, 'year') : seats * this.unitFor(plan, 'year')) +
        extraLocations * this.addonUnit('year');
      return {
        included,
        seats,
        extraLocations,
        planAmount,
        addon,
        total,
        monthlyTotal,
        yearlyTotal,
        save: monthlyTotal * 12 - yearlyTotal,
      };
    }

    render() {
      const cycle = this.cycle();
      const plan = this.plan();
      const plusLocked = plan === 'plus';

      if (plusLocked) {
        this.seatsInput.value = '1';
        this.seatsInput.min = '1';
        this.seatsInput.max = '1';
        this.seatsInput.disabled = true;
      } else {
        this.seatsInput.disabled = false;
        this.seatsInput.min = String(this.minSeats());
        this.seatsInput.max = String(this.maxSeats());
        const seats = Math.max(this.minSeats(), this.number(this.seatsInput) || this.lastTeamsSeats);
        this.seatsInput.value = String(this.clamp(seats, this.minSeats(), this.maxSeats()));
        this.lastTeamsSeats = this.number(this.seatsInput);
      }

      const locations = this.clamp(this.number(this.locationsInput), 0, this.maxLocations());
      this.locationsInput.value = String(locations);

      const result = this.compute(plan, cycle, this.number(this.seatsInput), locations);

      this.seatsField?.classList.toggle('is-locked', plusLocked);
      if (this.seatsHint) this.seatsHint.hidden = !plusLocked;
      this.paintSlider(this.seatsInput);
      this.paintSlider(this.locationsInput);

      if (this.displays.seats) this.displays.seats.textContent = this.integer(result.seats);
      if (this.displays.locations) this.displays.locations.textContent = this.integer(locations);
      if (this.displays.planAmount) this.displays.planAmount.textContent = this.money(result.planAmount);
      if (this.displays.included) {
        this.displays.included.textContent = this.replace(this.copy.included, {
          count: this.integer(result.included),
        });
      }
      if (this.displays.addon) this.displays.addon.textContent = this.money(result.addon);
      if (this.displays.total) this.displays.total.textContent = this.money(result.total);
      if (this.displays.period) {
        this.displays.period.textContent = cycle === 'year' ? this.copy.periodYear : this.copy.periodMonth;
      }

      if (this.addonRow) this.addonRow.hidden = result.extraLocations <= 0;
      if (this.hint) {
        this.hint.hidden = result.extraLocations <= 0;
        this.hint.textContent = this.replace(this.copy.extraHint, {
          count: this.integer(result.extraLocations),
          price: this.money(this.addonUnit(cycle)),
        });
      }

      if (this.effectiveRow && this.displays.effective) {
        this.effectiveRow.hidden = cycle !== 'year';
        this.displays.effective.textContent = this.replace(this.copy.effectiveMonth, {
          price: this.money(result.yearlyTotal / 12),
        });
      }

      if (this.saveRow && this.displays.save) {
        const showSave = cycle === 'year' && result.save > 0;
        this.saveRow.hidden = !showSave;
        this.displays.save.textContent = this.replace(this.copy.yearlySave, {
          price: this.money(result.save),
        });
      }

      this.updateCards(cycle);
      this.dataset.cycle = cycle;
      this.dataset.activePlan = plan;
    }

    updateCards(cycle) {
      const yearly = cycle === 'year';
      const plus = this.unitFor('plus', cycle);
      const teams = this.unitFor('teams', cycle);
      const teamsFrom = this.minSeats() * teams;

      if (this.cardAmounts.plus) this.cardAmounts.plus.textContent = this.money(plus);
      if (this.cardAmounts.teams) this.cardAmounts.teams.textContent = this.money(teams);
      if (this.cardPeriods.plus) {
        this.cardPeriods.plus.textContent = yearly ? this.copy.perYear : this.copy.perMonth;
      }
      if (this.cardPeriods.teams) {
        this.cardPeriods.teams.textContent = yearly ? this.copy.perSeatYear : this.copy.perSeatMonth;
      }
      if (this.cardFrom.teams) {
        this.cardFrom.teams.textContent = this.replace(yearly ? this.copy.fromYear : this.copy.fromMonth, {
          price: this.money(teamsFrom),
        });
      }
      this.cardDeals.forEach((el) => {
        el.textContent = yearly ? this.copy.yearDealOn : this.copy.yearDealOff;
      });
    }
  }

  customElements.define('up2-pricing', Up2Pricing);
}
